import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { loadCss, forceRound, getLowerCaseString, localize } from "../../helpers/function";
import LoadingTable from "../components/LoadingTable";
import { ToastBottomEnd } from "../components/Toast";
import {
  toastDeleteErrorMessageConfig,
  toastDeleteSuccessMessageConfig,
  toastErrorMessageWithParameterConfig,
} from "../../config/toastConfig";
import { DeleteConfirmation } from "../components/ConfirmationAlert";
import { confirmDeleteConfig } from "../../config/confirmConfig";
import appConfig from "../../config/appConfig";
import Pagination from "../components/Pagination";
import EmptyTable from "../components/EmptyTable";
import HeaderTablePage from "../components/HeaderTablePage";
import InfirmationTable from "../components/InfirmationTable";
import { ERROR_401 } from "../../config/errorPage";
import { verifyPermission } from "../../helpers/permission";
import { connect } from "react-redux";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { useTranslation } from "react-i18next";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const CategoryClient = (props) => {
  //usage of useTranslation i18n
  const { t, ready } = useTranslation();

  document.title =
    "Satis client - " + ready ? t("Paramètre categorie client") : "";
  if (!verifyPermission(props.userPermissions, "list-category-client"))
    window.location.href = ERROR_401;

  const [loading, setLoading] = useState(true);
  const [categoryClient, setCategoryClient] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // Calculate pagination values
  const currentPage = page;
  const totalPages = Math.ceil(total / perPage) || 1;
  const displayedCategories = categoryClient;

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  const loadCategoryClients = useCallback(async () => {
    if (!verifyTokenExpire()) return false;

    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page,
        size: perPage,
      });
      
      if (searchTerm) {
        params.append('search_text', searchTerm);
      }

      const response = await axios.get(`${appConfig.apiDomaine}/category-clients?${params}`);
      
      setCategoryClient(response.data.data || response.data);
      setTotal(response.data.total || response.data.length);
      return true;
    } catch (error) {
      console.error("Error fetching category clients:", error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchTerm]);

  // Reset page to 1 when search term or perPage changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, perPage]);

  useEffect(() => {
    loadCategoryClients();
  }, [loadCategoryClients]);

  const handleSearch = useCallback(
    (e) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handlePageChange = useCallback(
    (e, page) => {
      e.preventDefault();
      setPage(page);
    },
    []
  );

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleChangeItemsPerPage = useCallback((e) => {
    const value = parseInt(e.target.value);
    setPerPage(value);
  }, []);

  const deleteCategoryClient = useCallback((categoryClientId, index) => {
    DeleteConfirmation.fire(confirmDeleteConfig()).then((result) => {
      if (result.value) {
        if (verifyTokenExpire()) {
          axios
            .delete(
              appConfig.apiDomaine + `/category-clients/${categoryClientId}`
            )
            .then((response) => {
              // Refresh the data after deletion
              loadCategoryClients();
              ToastBottomEnd.fire(toastDeleteSuccessMessageConfig());
            })
            .catch((error) => {
              if (error.response?.data?.error)
                ToastBottomEnd.fire(
                  toastErrorMessageWithParameterConfig(
                    error.response.data.error
                  )
                );
              else ToastBottomEnd.fire(toastDeleteErrorMessageConfig());
            });
        }
      }
    });
  }, [loadCategoryClients]);

  const printBodyTable = (category, index) => {
    return (
      <tr key={index} role="row" className="odd">
        <td>
          {category && category.name && localize(category.name)
            ? localize(category.name)
            : ""}
        </td>
        <td>
          {category && category.description && localize(category.description)
            ? localize(category.description)
            : ""}
        </td>
        <td style={{ textAlign: "center" }}>
          {verifyPermission(props.userPermissions, "show-category-client") ? (
            <Link
              to={`/settings/clients/category/edit/${category.id}`}
              className="btn btn-sm btn-clean btn-icon btn-icon-md"
              title={t("Modifier")}
            >
              <i className="la la-edit" />
            </Link>
          ) : null}

          {verifyPermission(
            props.userPermissions,
            "destroy-category-client"
          ) ? (
            <button
              onClick={(e) => deleteCategoryClient(category.id, index)}
              className="btn btn-sm btn-clean btn-icon btn-icon-md"
              title={t("Supprimer")}
            >
              <i className="la la-trash" />
            </button>
          ) : null}
        </td>
      </tr>
    );
  };

  return ready ? (
    verifyPermission(props.userPermissions, "list-category-client") ? (
      <div
        className="kt-content  kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
        id="kt_content"
      >
        <div className="kt-subheader   kt-grid__item" id="kt_subheader">
          <div className="kt-container  kt-container--fluid ">
            <div className="kt-subheader__main">
              <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
              <span className="kt-subheader__separator kt-hidden" />
              <div className="kt-subheader__breadcrumbs">
                <a href="#icone" className="kt-subheader__breadcrumbs-home">
                  <i className="flaticon2-shelter" />
                </a>
                <span className="kt-subheader__breadcrumbs-separator" />
                <a
                  href="#button"
                  onClick={(e) => e.preventDefault()}
                  className="kt-subheader__breadcrumbs-link"
                >
                  {t("Client")}
                </a>
                <span className="kt-subheader__separator kt-hidden" />
                <div className="kt-subheader__breadcrumbs">
                  <a href="#icone" className="kt-subheader__breadcrumbs-home">
                    <i className="flaticon2-shelter" />
                  </a>
                  <span className="kt-subheader__breadcrumbs-separator" />
                  <a
                    href="#button"
                    onClick={(e) => e.preventDefault()}
                    className="kt-subheader__breadcrumbs-link"
                  >
                    {t("Catégorie client")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="kt-container  kt-container--fluid  kt-grid__item kt-grid__item--fluid">
          <InfirmationTable information={t("Liste des catégories client")} />

          <div className="kt-portlet">
            <HeaderTablePage
              addPermission={"store-category-client"}
              title={t("Catégorie client")}
              addText={verifyPermission(props.userPermissions, "store-category-client") && t("Ajouter")}
              addLink={verifyPermission(props.userPermissions, "store-category-client") && "/settings/clients/category/add"}
            />
            {loading ? (
              <LoadingTable />
            ) : (
              <div className="kt-portlet__body">
                <div
                  id="kt_table_1_wrapper"
                  className="dataTables_wrapper dt-bootstrap4"
                >
                  <div className="row">
                    <div className="col-sm-6 text-left">
                      <div id="kt_table_1_filter" className="dataTables_filter">
                        <label>
                          {t("Recherche")}:
                          <input
                            id="myInput"
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            className="form-control form-control-sm"
                            placeholder={t("Nom, description")}
                            aria-controls="kt_table_1"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      <table
                        className="table table-striped table-bordered table-hover table-checkable dataTable dtr-inline"
                        id="myTable"
                        role="grid"
                        aria-describedby="kt_table_1_info"
                        style={{ width: "952px" }}
                      >
                        <thead>
                          <tr role="row">
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "150px" }}
                              aria-label="Ship City: activate to sort column ascending"
                            >
                              {t("Nom")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "150px" }}
                              aria-label="Ship City: activate to sort column ascending"
                            >
                              {t("Description")}
                            </th>
                            <th
                              className="sorting"
                              tabIndex="0"
                              aria-controls="kt_table_1"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "70.25px" }}
                              aria-label="Type: activate to sort column ascending"
                            >
                              {t("Actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedCategories.length > 0 ? (
                            displayedCategories.map((category, index) =>
                              printBodyTable(category, index)
                            )
                          ) : (
                            <tr>
                              <td colSpan="3">
                                <EmptyTable search={searchTerm.length > 0} />
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr></tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12 col-md-5">
                      <div
                        className="dataTables_info"
                        id="kt_table_1_info"
                        role="status"
                        aria-live="polite"
                      >
                        {t("Affichage de")} {displayedCategories.length}{" "}
                        {t("sur")} {categoryClient.length} {t("données")}
                      </div>
                    </div>
                    {displayedCategories.length > 0 && (
                      <div className="col-sm-12 col-md-7 dataTables_pager">
                        <Pagination
                          numberPerPage={perPage}
                          onChangeNumberPerPage={handleChangeItemsPerPage}
                          activeNumberPage={currentPage}
                          onClickPreviousPage={handlePrevPage}
                          pages={pageNumbers}
                          onClickPage={handlePageChange}
                          numberPage={totalPages}
                          onClickNextPage={handleNextPage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ) : null
  ) : null;
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
  };
};

export default connect(mapStateToProps)(CategoryClient);
